import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { UserOptionalIcon } from '@modernloop/shared/icons';
import { Avatar, Stack, Typography } from '@mui/material';

import { AdditionalAttendeesDetails_AdditionalAttendeesFragment } from 'src/generated/mloop-graphql';

type Fragments = {
  additionalAttendees: AdditionalAttendeesDetails_AdditionalAttendeesFragment | null | undefined;
};

type Row = {
  key: string;
  name: string;
  pictureUrl?: string | null;
  isOptional: boolean;
};

/**
 * Read-back of the additional attendees snapshotted on an interview, candidate event or debrief.
 *
 * Each recipient carries its own role, so the UserOptionalIcon is per row. The collection-level
 * `isOptional` is only read when `attendees` is absent, which the server never does in practice --
 * rows written before per-recipient roles existed answer with entries materialized from the legacy
 * fields.
 */
const AdditionalAttendeesDetails: FCWithFragments<Fragments> = ({ additionalAttendees }) => {
  const { t } = useTranslation();

  const rows: Row[] = additionalAttendees?.attendees
    ? additionalAttendees.attendees.map((attendee, index) => ({
        key: attendee.employeeId ?? attendee.email ?? `attendee-${index}`,
        name: attendee.employee?.fullName || attendee.employee?.email || attendee.email || '',
        pictureUrl: attendee.employee?.internalProfilePictureUrl,
        isOptional: attendee.isOptional,
      }))
    : [
        ...(additionalAttendees?.employees ?? []).map((employee) => ({
          key: employee.id,
          name: employee.fullName || employee.email || '',
          pictureUrl: employee.internalProfilePictureUrl,
          isOptional: Boolean(additionalAttendees?.isOptional),
        })),
        ...(additionalAttendees?.emails ?? []).map((email) => ({
          key: email,
          name: email,
          isOptional: Boolean(additionalAttendees?.isOptional),
        })),
      ];

  if (!rows.length) {
    return null;
  }

  return (
    <Stack spacing={1} data-testid="additional-attendees-details">
      <Typography variant="caption" color="text.secondary">
        {t('hiring-dashboard-schedule:schedule.content.additionalAttendees')}
      </Typography>

      {rows.map((row) => (
        <Stack key={row.key} direction="row" alignItems="center" spacing={1}>
          <Avatar src={row.pictureUrl || ''}>{row.name.charAt(0)}</Avatar>
          <Typography variant="body2" data-testid="additional-attendees-details-name">
            {row.name}
          </Typography>
          {row.isOptional && (
            <UserOptionalIcon
              data-testid="additional-attendees-details-optional-icon"
              tooltip={t('hiring-dashboard-schedule:schedule.content.optional')}
            />
          )}
        </Stack>
      ))}
    </Stack>
  );
};

AdditionalAttendeesDetails.fragments = {
  additionalAttendees: gql`
    fragment AdditionalAttendeesDetails_additionalAttendees on AdditionalAttendees {
      emails
      isOptional
      attendees {
        email
        employeeId
        isOptional
        employee {
          id
          fullName
          email
          internalProfilePictureUrl
        }
      }
      employees {
        id
        fullName
        email
        internalProfilePictureUrl
      }
    }
  `,
};

export default AdditionalAttendeesDetails;
